/**
 * 앞 낱말의 받침을 보고 조사를 고른다.
 *
 * `을(를)`, `이(가)` 는 서식 문서의 말투다. 대화 상자에서 사람에게 말할 때 쓰면
 * 기계가 낸 문장으로 읽힌다 — `지급유형을 알려 주세요` 와 `지급유형을(를) 알려
 * 주세요` 는 같은 뜻이지만 받는 느낌이 다르다.
 *
 * 한글이 아닌 것으로 끝나면(코드, 숫자, 영문) 고를 근거가 없다. 그때는 둘 다 적어
 * 두는 편이 틀리게 고르는 것보다 낫다.
 */
export function withParticle(word: string, withFinal: string, withoutFinal: string): string {
  const last = word.trim().slice(-1);
  const code = last.charCodeAt(0);
  if (Number.isNaN(code) || code < 0xac00 || code > 0xd7a3) {
    return `${word}${withFinal}(${withoutFinal})`;
  }
  // 한글 음절은 (초성, 중성, 종성) 셋으로 자리를 나눠 담는다. 종성이 0이면 받침이 없다.
  const hasFinal = (code - 0xac00) % 28 !== 0;
  return `${word}${hasFinal ? withFinal : withoutFinal}`;
}

/** `제목을`, `기간를` 을 가른다. */
export function objectParticle(word: string): string {
  return withParticle(word, '을', '를');
}

/** `제목이`, `기간가` 를 가른다. */
export function subjectParticle(word: string): string {
  return withParticle(word, '이', '가');
}

/** `도어센서는`, `본사은` 을 가른다. */
export function topicParticle(word: string): string {
  return withParticle(word, '은', '는');
}

/**
 * 문장·표 칸의 큰 숫자에 천 단위 쉼표를 찍는다.
 *
 * `총 매출 455666061원` 은 사무직이 자릿수를 세어 읽어야 하는 숫자다. 답변 프롬프트에
 * `1,234,567 처럼 쓰라` 고 적어 두었지만, 계산이 만든 요약 문장은 그대로 실려 나가서
 * 그 규칙이 닿지 않았다.
 *
 * **건드리지 않는 것**: 앞뒤에 숫자·쉼표·마침표·붙임표가 붙은 것(이미 찍혔거나 날짜),
 * `0` 으로 시작하는 것(전화번호·코드), 그리고 `202608`·`20260812` 처럼 연월일로 보이는
 * 여섯·여덟 자리. 다섯 자리(만 원) 이상만 본다 — 네 자리는 연도와 구별할 수 없다.
 */
export function withThousands(text: string): string {
  return text.replace(/(^|[^\d,.\-])(\d{5,})(?![\d,.\-])/g, (whole, before: string, digits: string) => {
    if (digits.charAt(0) === '0') return whole;
    const periodish = (digits.length === 6 || digits.length === 8) && /^(?:19|20)/.test(digits);
    if (periodish) return whole;
    let grouped = '';
    for (let index = digits.length; index > 0; index -= 3) {
      const start = Math.max(0, index - 3);
      grouped = digits.slice(start, index) + (grouped ? `,${grouped}` : '');
    }
    return before + grouped;
  });
}
